import React from "react";
import Box from "@mui/material/Box";

type CardBrandIconsProps = {
  /** Smaller icons for inside the card number field */
  compact?: boolean;
};

const VisaIcon: React.FunctionComponent<{ width: number; height: number }> = ({
  width,
  height,
}) => (
  <Box
    component="svg"
    viewBox="0 0 48 32"
    aria-hidden
    sx={{ width, height, display: "block", flexShrink: 0 }}
  >
    <rect width="48" height="32" rx="4" fill="#1A1F71" />
    <text
      x="24"
      y="21"
      textAnchor="middle"
      fill="#fff"
      fontSize="11"
      fontWeight="700"
      fontFamily="Arial, sans-serif"
    >
      VISA
    </text>
  </Box>
);

const MastercardIcon: React.FunctionComponent<{ width: number; height: number }> = ({
  width,
  height,
}) => (
  <Box
    component="svg"
    viewBox="0 0 48 32"
    aria-hidden
    sx={{ width, height, display: "block", flexShrink: 0 }}
  >
    <rect width="48" height="32" rx="4" fill="#252525" />
    <circle cx="19" cy="16" r="9" fill="#EB001B" />
    <circle cx="29" cy="16" r="9" fill="#F79E1B" fillOpacity="0.95" />
  </Box>
);

const AmexIcon: React.FunctionComponent<{ width: number; height: number }> = ({
  width,
  height,
}) => (
  <Box
    component="svg"
    viewBox="0 0 48 32"
    aria-hidden
    sx={{ width, height, display: "block", flexShrink: 0 }}
  >
    <rect width="48" height="32" rx="4" fill="#006FCF" />
    <text
      x="24"
      y="14"
      textAnchor="middle"
      fill="#fff"
      fontSize="7"
      fontWeight="700"
      fontFamily="Arial, sans-serif"
    >
      AMERICAN
    </text>
    <text
      x="24"
      y="23"
      textAnchor="middle"
      fill="#fff"
      fontSize="7"
      fontWeight="700"
      fontFamily="Arial, sans-serif"
    >
      EXPRESS
    </text>
  </Box>
);

export type KnownCardBrand = "visa" | "mastercard" | "amex";

/** Map stored bankName / Stripe brand to a known network icon. */
export const parseCardBrand = (bankName?: string | null): KnownCardBrand | null => {
  const normalized = (bankName || "").trim().toLowerCase().replace(/[\s_-]/g, "");
  if (!normalized) {
    return null;
  }
  if (normalized === "visa") {
    return "visa";
  }
  if (normalized === "mastercard" || normalized === "mc") {
    return "mastercard";
  }
  if (
    normalized === "amex" ||
    normalized === "americanexpress" ||
    normalized === "americanexpresscard"
  ) {
    return "amex";
  }
  return null;
};

type CardBrandIconProps = {
  brand?: string | null;
  compact?: boolean;
};

/** Single network mark for a saved card (Visa, Mastercard, or Amex). */
export const CardBrandIcon: React.FunctionComponent<CardBrandIconProps> = ({
  brand,
  compact = false,
}) => {
  const width = compact ? 32 : 40;
  const height = compact ? 20 : 24;
  const known = parseCardBrand(brand);

  const ariaLabel =
    known === "visa"
      ? "Visa"
      : known === "mastercard"
        ? "Mastercard"
        : known === "amex"
          ? "American Express"
          : "Card";

  return (
    <Box sx={{ flexShrink: 0 }} aria-label={ariaLabel}>
      {known === "visa" && <VisaIcon width={width} height={height} />}
      {known === "mastercard" && <MastercardIcon width={width} height={height} />}
      {known === "amex" && <AmexIcon width={width} height={height} />}
      {!known && (
        <Box
          sx={{
            width,
            height,
            borderRadius: 0.5,
            bgcolor: "grey.300",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: compact ? 9 : 10,
            fontWeight: 700,
            color: "text.secondary",
          }}
        >
          CARD
        </Box>
      )}
    </Box>
  );
};

/** Visa, Mastercard, and American Express marks for checkout card entry. */
export const CardBrandIcons: React.FunctionComponent<CardBrandIconsProps> = ({
  compact = false,
}) => {
  const width = compact ? 32 : 40;
  const height = compact ? 20 : 24;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: compact ? 0.5 : 1,
        flexShrink: 0,
      }}
      aria-label="Visa, Mastercard, American Express accepted"
    >
      <VisaIcon width={width} height={height} />
      <MastercardIcon width={width} height={height} />
      <AmexIcon width={width} height={height} />
    </Box>
  );
};

export default CardBrandIcons;
